import React, { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useSearchParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { formatRelativeTime, formatClockTime, formatFileSize } from '../../utils/format'
import { NSModal, NSField, NSSelect, NSSegment, NSChip, NSDrop } from '../shared/ns-kit'
import { I } from '../shared/ns-icons'
import { nsConfirm } from '../shared/ns-confirm'

// Resolve a stored "/uploads/…" path to a fetchable URL (dev points at the backend port)
const fileUrl = (p) => {
  if (!p) return ''
  if (/^https?:\/\//.test(p)) return p
  const base = import.meta.env.PROD ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000')
  return `${base}${p.startsWith('/') ? '' : '/'}${p}`
}

// ── Gradient helper (same pattern as siteGradient) ────────────────────────────
const REQ_GRADIENTS = [
  ['#7C5CFF', '#5B7CFF'],
  ['#A78BFA', '#7C5CFF'],
  ['#EC4899', '#A78BFA'],
  ['#FF8A65', '#F76C5E'],
  ['#2BD9A6', '#06B6D4'],
  ['#06B6D4', '#5B7CFF'],
  ['#FFB454', '#FF8A65'],
  ['#5BC0EB', '#5B7CFF'],
]

function reqGradient(id) {
  const hash = (id || '').toString().split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  return REQ_GRADIENTS[hash % REQ_GRADIENTS.length]
}

function getInitials(name) {
  if (!name) return '?'
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
}

function titleCase(s) {
  if (!s) return '—'
  if (s.length > 16) return 'Custom'
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ── Priority & status maps ────────────────────────────────────────────────────
const PRI_TONE = { high: 'danger', urgent: 'danger', medium: 'warning', normal: 'warning', low: 'info' }
const PRI_LABEL = { high: 'High', urgent: 'Urgent', medium: 'Medium', normal: 'Normal', low: 'Low' }

const STATUS_CONFIG = {
  open:        { label: 'Open',        tone: 'warning' },
  pending:     { label: 'Open',        tone: 'warning' },
  'in-progress': { label: 'In Progress', tone: 'info' },
  approved:    { label: 'In Progress', tone: 'info' },
  'in_progress': { label: 'In Progress', tone: 'info' },
  'in-review': { label: 'In Review',   tone: 'accent' },
  completed:   { label: 'Completed',   tone: 'success' },
  rejected:    { label: 'Rejected',    tone: 'danger' },
  archived:    { label: 'Archived',    tone: '' },
}

const TONE_VAR = { success: '--success', danger: '--danger', info: '--accent', accent: '--accent', warning: '--warning' }

const KANBAN_COLS = [
  { id: 'open',        label: 'Open',        tone: 'warning',  statuses: ['open', 'pending'] },
  { id: 'in-progress', label: 'In Progress', tone: 'info',     statuses: ['in-progress', 'approved', 'in_progress'] },
  { id: 'in-review',   label: 'In Review',   tone: 'accent',   statuses: ['in-review'] },
  { id: 'completed',   label: 'Completed',   tone: 'success',  statuses: ['completed'] },
]

// Status assigned to a new ticket raised directly into a board column
const STATUS_FOR_COL = { open: 'pending', 'in-progress': 'in-progress', 'in-review': 'in-review', completed: 'completed' }

// Status segmented options shown in the drawer
const STATUS_SEG = [['pending', 'Open'], ['in-progress', 'In progress'], ['in-review', 'In review'], ['completed', 'Completed']]
const colOf = (status) => (KANBAN_COLS.find(c => c.statuses.includes(status)) || {}).id

const ACTION_LABEL = {
  created: 'Request created', assigned: 'Assigned', approved: 'Approved',
  rejected: 'Rejected', completed: 'Completed', updated: 'Details updated', started: 'Work started',
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const IconPlus = ({ size = 13 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
)
const IconFilter = ({ size = 13 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
)
const IconClose = ({ size = 14 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
)
const IconCheck = ({ size = 10 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
)
const IconAttach = ({ size = 13 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" /></svg>
)
const IconEdit = ({ size = 13 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
)
const IconSend = ({ size = 13 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
)
const IconFile = ({ size = 14 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
)
const IconImage = ({ size = 14 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
)
const IconTrash = ({ size = 13 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
)
const IconArchive = ({ size = 13 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></svg>
)
const IconRestore = ({ size = 13 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 102.13-9.36L1 10" /></svg>
)

// Attachment chip — a link when viewing an uploaded file, removable when staging a new one.
// `stretch` makes it fill the parent column so chips under a message match the bubble width.
function AttachChip({ att, onRemove, stretch }) {
  const name = att.file_name || att.name
  const isImg = (att.mime_type || att.type || '').startsWith('image/')
  const sizeBytes = att.file_size != null ? att.file_size : att.size
  const stretchStyle = stretch ? { display: 'flex', width: '100%' } : null
  const inner = (
    <>
      <span className="a-icon">{isImg ? <IconImage /> : <IconFile />}</span>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <span style={{ color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
        {sizeBytes != null && <span style={{ fontSize: 10.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{formatFileSize(sizeBytes)}</span>}
      </div>
    </>
  )
  if (onRemove) {
    return (
      <div className="attach-chip" style={stretchStyle}>
        {inner}
        <button type="button" className="site-action" onClick={onRemove} title="Remove" style={{ width: 20, height: 20, marginLeft: 2 }}><IconClose size={11} /></button>
      </div>
    )
  }
  return (
    <a className="attach-chip" href={fileUrl(att.file_path)} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', ...stretchStyle }}>{inner}</a>
  )
}

// ── Avatar component (round, matching prototype) ──────────────────────────────
function Avatar({ name, id, size = 26, style: extraStyle = {} }) {
  const [from, to] = reqGradient(id || name)
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: Math.max(8, size * 0.36),
      ...extraStyle,
    }}>
      {getInitials(name)}
    </div>
  )
}

// ── Kanban card ───────────────────────────────────────────────────────────────
function KanbanCard({ request, onClick }) {
  const priTone = PRI_TONE[request.priority] || 'warning'
  const priLabel = PRI_LABEL[request.priority] || request.priority || '—'
  const clientName = request.company_name || request.client_name || '—'
  const desc = request.description || request.desc || ''

  return (
    <div className="card card-pad-sm request-card" style={{ borderRadius: 12, cursor: 'pointer' }} onClick={() => onClick(request)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Avatar name={clientName} id={request.client_id || request.id} size={22} />
        <span style={{ fontSize: 11.5, color: 'var(--text-muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {clientName}
        </span>
        <span className={`badge badge-${priTone}`} style={{ fontSize: 10 }}>{priLabel}</span>
      </div>

      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, lineHeight: 1.35 }}>
        {request.title}
      </div>

      {desc && (
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {desc}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
        {request.assigned_to_name ? (
          <Avatar name={request.assigned_to_name} id={request.assigned_to} size={20} />
        ) : (
          <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--surface)', border: '1.5px dashed var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 9 }}>?</div>
        )}
        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          {request.assigned_to_name || 'Unassigned'}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>
          {request.created_at ? formatRelativeTime(request.created_at) : '—'}
        </span>
      </div>
    </div>
  )
}

// ── Request drawer (1:1 with prototype RequestDetail) ─────────────────────────
function RequestDrawer({ requestId, onClose, onUpdated }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [reply, setReply] = useState('')
  const [files, setFiles] = useState([])
  const fileInputRef = useRef(null)

  const { data: request, isLoading } = useQuery(
    ['request-detail', requestId],
    () => apiService.get(`/requests/${requestId}`).then(res => res.data),
    { enabled: !!requestId }
  )

  const { data: messages } = useQuery(
    ['request-messages', requestId],
    () => apiService.get(`/requests/${requestId}/messages`).then(res => res.data),
    { enabled: !!requestId }
  )

  const { data: logs } = useQuery(
    ['request-logs', requestId],
    () => apiService.get(`/requests/${requestId}/logs`).then(res => res.data),
    { enabled: !!requestId }
  )

  const { data: users } = useQuery('users', () => apiService.get('/users').then(res => res.data), { staleTime: 5 * 60 * 1000 })
  const admins = (Array.isArray(users) ? users : []).filter(u => u.role === 'admin')

  const statusMutation = useMutation(
    (status) => apiService.put(`/requests/${requestId}`, { status }),
    {
      onSuccess: (_data, status) => {
        toast.success(status === 'archived' ? 'Request archived' : 'Status updated')
        queryClient.invalidateQueries(['request-detail', requestId])
        queryClient.invalidateQueries(['request-logs', requestId])
        queryClient.invalidateQueries('requests')
        queryClient.invalidateQueries(['requests', 'archived'])
        onUpdated()
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to update status'),
    }
  )

  const deleteMutation = useMutation(
    () => apiService.delete(`/requests/${requestId}`),
    {
      onSuccess: () => {
        toast.success('Request deleted')
        queryClient.invalidateQueries('requests')
        queryClient.invalidateQueries(['requests', 'archived'])
        onClose()
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to delete request'),
    }
  )

  const handleDelete = async () => {
    const ok = await nsConfirm({
      tone: 'danger',
      icon: 'trash',
      title: 'Delete this request?',
      message: 'This permanently removes the request along with its conversation, attachments and activity log. This cannot be undone.',
      confirmLabel: 'Delete request',
    })
    if (ok) deleteMutation.mutate()
  }

  const assignMutation = useMutation(
    (assigned_to) => apiService.post(`/requests/${requestId}/assign`, { assigned_to }),
    {
      onSuccess: () => {
        toast.success('Assignee updated')
        queryClient.invalidateQueries(['request-detail', requestId])
        queryClient.invalidateQueries(['request-logs', requestId])
        queryClient.invalidateQueries('requests')
        onUpdated()
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to assign'),
    }
  )

  const sendMutation = useMutation(
    ({ body, files }) => {
      if (files && files.length) {
        const fd = new FormData()
        if (body) fd.append('body', body)
        files.forEach(f => fd.append('files', f))
        return apiService.post(`/requests/${requestId}/messages`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      }
      return apiService.post(`/requests/${requestId}/messages`, { body })
    },
    {
      onSuccess: () => {
        setReply('')
        setFiles([])
        queryClient.invalidateQueries(['request-messages', requestId])
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to send reply'),
    }
  )

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || [])
    if (picked.length) setFiles(prev => [...prev, ...picked].slice(0, 5))
    e.target.value = '' // reset so the same file can be re-selected
  }

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const clientName = request?.company_name || request?.client_name || '—'
  const status = request?.status || 'open'
  const priTone = PRI_TONE[request?.priority] || 'warning'
  const priLabel = PRI_LABEL[request?.priority] || request?.priority || '—'
  const statusCfg = STATUS_CONFIG[status] || { label: status, tone: 'warning' }
  const refLabel = request?.ref_number ? `REF-${request.ref_number}` : '…'

  // Conversation: the original request shown as the opening client message, then replies
  const conversation = []
  if (request?.description) {
    conversation.push({
      id: '__initial', author_role: 'client',
      author_name: request.created_by_name || clientName,
      user_id: request.created_by, body: request.description, created_at: request.created_at,
      // Surface the originally-submitted screenshot as an attachment on the opening message
      attachments: request.screenshot_path
        ? [{ id: '__screenshot', file_path: request.screenshot_path, file_name: 'Submitted screenshot', mime_type: 'image/png' }]
        : [],
    })
  }
  if (Array.isArray(messages)) messages.forEach(m => conversation.push(m))

  // Every attachment posted across the conversation (shown as one section above Activity)
  const allAttachments = conversation.flatMap(m => Array.isArray(m.attachments) ? m.attachments : [])

  // Activity timeline derived from the request action logs (oldest → newest)
  const activity = (Array.isArray(logs) ? [...logs].reverse() : []).map((l, i, arr) => ({
    text: l.notes || ACTION_LABEL[l.action] || l.action,
    time: l.created_at ? formatRelativeTime(l.created_at) : '',
    dot: i === arr.length - 1 ? 'active' : 'done',
  }))

  const openInEditor = () => {
    if (!request?.site_id) return
    navigate(`/admin/editor?site=${request.site_id}${request.client_id ? `&client=${request.client_id}` : ''}`)
  }

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={e => e.stopPropagation()}>
        {/* Head */}
        <div className="drawer-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-faint)' }}>{refLabel}</span>
            {request?.priority && (
              <span className={`badge badge-${priTone}`} style={{ fontSize: 10 }}>{priLabel} priority</span>
            )}
            {request?.category && (
              <span className="badge" style={{ fontSize: 10 }}>{request.category}</span>
            )}
            <button className="site-action" style={{ marginLeft: 'auto' }} onClick={onClose} title="Close"><IconClose /></button>
          </div>
          {isLoading ? (
            <div style={{ height: 26, background: 'var(--surface-2)', borderRadius: 6 }} />
          ) : (
            <h2 style={{ fontSize: 19, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.3 }}>{request?.title || 'Loading…'}</h2>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <Avatar name={clientName} id={request?.client_id} size={24} />
            <span style={{ fontSize: 13, color: 'var(--text-soft)' }}>{clientName}</span>
            {request?.site_name && (
              <>
                <span style={{ color: 'var(--text-faint)' }}>·</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{request.site_name}</span>
              </>
            )}
            {request?.created_at && (
              <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{formatRelativeTime(request.created_at)}</span>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="drawer-body">
          {/* Status control */}
          <div>
            <div className="drawer-section-label">Status</div>
            <div className="segmented" style={{ width: '100%' }}>
              {STATUS_SEG.map(([v, l]) => (
                <button
                  key={v}
                  className={status !== 'archived' && colOf(status) === colOf(v) ? 'on' : ''}
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={() => statusMutation.mutate(v)}
                  disabled={statusMutation.isLoading}
                >
                  {l}
                </button>
              ))}
            </div>
            {status === 'archived' && (
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="badge">Archived</span>
                This request is archived. Restore it to move it back onto the board.
              </div>
            )}
            {/* Archive / restore + delete (delete lives only here, in the drawer) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              {status === 'archived' ? (
                <button className="btn btn-sm" onClick={() => statusMutation.mutate('completed')} disabled={statusMutation.isLoading}>
                  <IconRestore /> Restore
                </button>
              ) : (
                <button className="btn btn-sm" onClick={() => statusMutation.mutate('archived')} disabled={statusMutation.isLoading}>
                  <IconArchive /> Archive now
                </button>
              )}
              <button
                className="btn btn-sm btn-ghost"
                style={{ marginLeft: 'auto', color: 'var(--danger)' }}
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
              >
                <IconTrash /> Delete
              </button>
            </div>
          </div>

          {/* Meta grid */}
          {request && (
            <div className="detail-meta-grid">
              <div>
                <div className="k">Assignee</div>
                <div className="v">
                  <NSSelect
                    value={request.assigned_to || ''}
                    onChange={e => assignMutation.mutate(e.target.value)}
                    disabled={assignMutation.isLoading}
                    placeholder="Unassigned · assign…"
                    options={admins.map(a => ({ value: a.id, label: a.name }))}
                  />
                </div>
              </div>
              <div>
                <div className="k">Requested by</div>
                <div className="v">{request.created_by_name || '—'}</div>
              </div>
              <div>
                <div className="k">Plan</div>
                <div className="v"><span className="badge badge-accent" style={{ fontSize: 10 }}>{titleCase(request.plan)}</span></div>
              </div>
              <div>
                <div className="k">Current status</div>
                <div className="v"><span className={`badge badge-${statusCfg.tone}`}><span className="badge-dot" /> {statusCfg.label}</span></div>
              </div>
            </div>
          )}

          {/* Conversation */}
          <div>
            <div className="drawer-section-label">Conversation</div>
            <div className="thread">
              {conversation.map((m, i) => {
                const isYou = m.author_role === 'admin'
                return (
                  <div key={m.id || i} className={`msg ${isYou ? 'you' : ''}`}>
                    <Avatar name={m.author_name} id={m.user_id || m.author_name} size={28} />
                    <div className="msg-body">
                      <div className="msg-meta">
                        <strong style={{ color: 'var(--text-soft)' }}>{isYou ? `${m.author_name} · WebNest` : m.author_name}</strong>
                        <span style={{ fontFamily: 'var(--font-mono)' }}>{m.created_at ? formatClockTime(m.created_at) : ''}</span>
                      </div>
                      {m.body && <div className="msg-bubble">{m.body}</div>}
                      {Array.isArray(m.attachments) && m.attachments.map(a => (
                        <AttachChip key={a.id} att={a} stretch />
                      ))}
                    </div>
                  </div>
                )
              })}
              {conversation.length === 0 && (
                <div style={{ fontSize: 12.5, color: 'var(--text-faint)', padding: '4px 0' }}>No messages yet.</div>
              )}
            </div>
          </div>

          {/* Attachments — every file shared across the conversation */}
          {allAttachments.length > 0 && (
            <div>
              <div className="drawer-section-label">Attachments · {allAttachments.length}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {allAttachments.map(a => <AttachChip key={a.id} att={a} />)}
              </div>
            </div>
          )}

          {/* Activity */}
          {activity.length > 0 && (
            <div>
              <div className="drawer-section-label">Activity</div>
              <div className="vtl">
                {activity.map((a, i) => (
                  <div key={i} className="vtl-row">
                    <span className={`vtl-dot ${a.dot}`}>{a.dot === 'done' ? <IconCheck /> : ''}</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 1 }}>
                      <span style={{ fontSize: 12.5, color: 'var(--text-soft)' }}>{a.text}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{a.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer reply */}
        <div className="drawer-foot">
          <div className="reply-box">
            <textarea
              placeholder={`Reply to ${request?.created_by_name || 'client'}…`}
              value={reply}
              onChange={e => setReply(e.target.value)}
            />
            {files.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0 12px 10px' }}>
                {files.map((f, i) => (
                  <AttachChip key={i} att={f} onRemove={() => setFiles(prev => prev.filter((_, j) => j !== i))} />
                ))}
              </div>
            )}
            <div className="reply-tools">
              <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={onPickFiles} />
              <button className="btn btn-sm btn-ghost" type="button" onClick={() => fileInputRef.current?.click()}><IconAttach /> Attach</button>
              <button className="btn btn-sm btn-ghost" type="button" onClick={openInEditor}><IconEdit /> Open in editor</button>
              <button
                className="btn btn-sm btn-primary"
                style={{ marginLeft: 'auto' }}
                disabled={(!reply.trim() && files.length === 0) || sendMutation.isLoading}
                onClick={() => sendMutation.mutate({ body: reply.trim(), files })}
              >
                <IconSend /> Send reply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Filters popover ─────────────────────────────────────────────────────────
function FiltersPopover({ priority, setPriority, category, setCategory, status, setStatus, onClose }) {
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 50 }} onClick={onClose} />
      <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 51, width: 280, background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow)', padding: 14 }}>
        <div style={{ fontSize: 10.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Status</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          {[['', 'All'], ['pending', 'Open'], ['in-progress', 'In progress'], ['in-review', 'In review'], ['completed', 'Completed'], ['archived', 'Archived']].map(([v, l]) => (
            <button
              key={v}
              className={`badge ${status === v ? 'badge-accent' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setStatus(v)}
            >
              {l}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--text-faint)', marginBottom: 16 }}>Archived requests appear in the List view only.</div>
        <div style={{ fontSize: 10.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Priority</div>
        <div className="segmented" style={{ width: '100%', marginBottom: 16 }}>
          {[['', 'All'], ['high', 'High'], ['medium', 'Med'], ['low', 'Low']].map(([v, l]) => (
            <button key={v} className={priority === v ? 'on' : ''} style={{ flex: 1, justifyContent: 'center' }} onClick={() => setPriority(v)}>{l}</button>
          ))}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Category</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {[['', 'All'], ['content', 'Content'], ['design', 'Design'], ['functionality', 'Functionality'], ['bug', 'Bug'], ['other', 'Other']].map(([v, l]) => (
            <button
              key={v}
              className={`badge ${category === v ? 'badge-accent' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setCategory(v)}
            >
              {l}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-sm" onClick={() => { setPriority(''); setCategory(''); setStatus('') }}>Clear all</button>
        </div>
      </div>
    </>
  )
}

// ── New request modal (raise a ticket on behalf of a client) ─────────────────
// Type chip label → backend category value
const REQ_TYPES = [
  { label: 'Content', value: 'content' },
  { label: 'Design', value: 'design' },
  { label: 'Bug fix', value: 'bug' },
  { label: 'New page', value: 'functionality' },
  { label: 'SEO', value: 'other' },
]
// Priority segment label → backend priority value
const PRIORITY_MAP = { Low: 'low', Normal: 'medium', High: 'high' }

function NewRequestModal({ initialStatus = 'pending', initialClientId = null, onClose, onCreated }) {
  const [siteId, setSiteId] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priorityLabel, setPriorityLabel] = useState('Normal')
  const [category, setCategory] = useState('content')
  const [assignedTo, setAssignedTo] = useState('')
  const [files, setFiles] = useState([])
  const status = initialStatus // set by the entry point (board column +); not user-editable here

  const { data: sitesData } = useQuery('sites', () => apiService.get('/sites').then(r => r.data), { staleTime: 5 * 60 * 1000 })
  const sites = sitesData?.sites || []

  // Team members eligible for assignment (admins + editors)
  const { data: usersData } = useQuery('staff-users', () => apiService.get('/users').then(r => r.data), { staleTime: 5 * 60 * 1000 })
  const staff = (Array.isArray(usersData) ? usersData : []).filter(u => u.role === 'admin' || u.role === 'editor')
  const assigneeOptions = [{ value: '', label: 'Unassigned' }, ...staff.map(u => ({ value: u.id, label: u.name || u.email }))]

  // Preselect the first site of the requested client (from ?newFor=…)
  useEffect(() => {
    if (initialClientId && sites.length && !siteId) {
      const match = sites.find(s => s.client_id === initialClientId)
      if (match) setSiteId(match.id)
    }
  }, [initialClientId, sites]) // eslint-disable-line react-hooks/exhaustive-deps

  // Default the site select to the first available site so it matches the visible value
  useEffect(() => {
    if (!siteId && sites.length) setSiteId(sites[0].id)
  }, [sites]) // eslint-disable-line react-hooks/exhaustive-deps

  const siteOptions = sites.map(s => ({ value: s.id, label: s.company_name ? `${s.company_name} — ${s.name}` : s.name }))

  const createMutation = useMutation(
    (payload) => apiService.post('/requests', payload, { headers: { 'Content-Type': 'multipart/form-data' } }),
    {
      onSuccess: () => { toast.success('Request created'); onCreated(); onClose() },
      onError: (e) => toast.error(e.response?.data?.error || e.response?.data?.errors?.[0]?.msg || 'Failed to create request'),
    }
  )

  const submit = () => {
    if (!siteId) { toast.error('Select a client site'); return }
    if (!title.trim()) { toast.error('Enter a title'); return }
    if (!description.trim()) { toast.error('Enter a description'); return }
    const fd = new FormData()
    fd.append('site_id', siteId)
    fd.append('title', title.trim())
    fd.append('description', description.trim())
    fd.append('priority', PRIORITY_MAP[priorityLabel] || 'medium')
    fd.append('category', category)
    fd.append('status', status)
    if (assignedTo) fd.append('assigned_to', assignedTo)
    if (files[0]) fd.append('screenshot', files[0])
    createMutation.mutate(fd)
  }

  return (
    <NSModal
      icon="message" tone="accent" onClose={onClose} width={640}
      title="New request" subtitle="Log a change request and route it to your team."
      foot={
        <>
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <I.bolt style={{ color: 'var(--accent-3)' }} /> Client is notified the moment work begins
          </span>
          <button className="btn" style={{ marginLeft: 'auto' }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={createMutation.isLoading}>
            <I.plus /> {createMutation.isLoading ? 'Creating…' : 'Create request'}
          </button>
        </>
      }
    >
      <NSField label="Title">
        <input className="input" autoFocus value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Update the winter menu prices" />
      </NSField>

      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <NSField label="Site">
          <NSSelect value={siteId} onChange={e => setSiteId(e.target.value)} options={siteOptions} />
        </NSField>
        <NSField label="Assignee">
          <NSSelect value={assignedTo} onChange={e => setAssignedTo(e.target.value)} options={assigneeOptions} />
        </NSField>
      </div>

      <NSField label="Type">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {REQ_TYPES.map(t => <NSChip key={t.value} label={t.label} on={category === t.value} onClick={() => setCategory(t.value)} />)}
        </div>
      </NSField>

      <NSField label="Priority">
        <NSSegment options={['Low', 'Normal', 'High']} value={priorityLabel} onChange={setPriorityLabel} />
      </NSField>

      <NSField label="Description" hint="what should change & why">
        <textarea className="input" rows={4} style={{ resize: 'vertical', lineHeight: 1.6 }} value={description} onChange={e => setDescription(e.target.value)}
          placeholder="Describe the change in detail. Include page URLs, copy, or links to reference…" />
      </NSField>

      <NSField label="Attachments" hint="optional">
        <NSDrop icon="image" title={files.length ? `${files.length} file${files.length > 1 ? 's' : ''} selected` : 'Drop screenshots or files here'} hint="PNG, JPG, PDF · max 20 MB each" height={104} onFiles={fs => setFiles(fs)} />
      </NSField>
    </NSModal>
  )
}

// ── List view ─────────────────────────────────────────────────────────────────
const LIST_COLS = '2.4fr 1fr 1fr 1.2fr 0.7fr'

function RequestList({ requests, onOpen }) {
  if (requests.length === 0) return <div className="req-dropzone" style={{ marginTop: 4 }}>No requests</div>
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: LIST_COLS, gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border)', fontSize: 10.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        <span>Request</span><span>Priority</span><span>Status</span><span>Assigned</span><span style={{ textAlign: 'right' }}>Age</span>
      </div>
      {requests.map(r => {
        const statusCfg = STATUS_CONFIG[r.status] || { label: r.status, tone: 'warning' }
        const priTone = PRI_TONE[r.priority] || 'warning'
        const priLabel = PRI_LABEL[r.priority] || r.priority || '—'
        const clientName = r.company_name || r.client_name || '—'
        return (
          <div
            key={r.id}
            className="req-list-row"
            onClick={() => onOpen(r.id)}
            style={{ display: 'grid', gridTemplateColumns: LIST_COLS, gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center', cursor: 'pointer' }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{clientName}</div>
            </div>
            <span><span className={`badge badge-${priTone}`} style={{ fontSize: 10 }}>{priLabel}</span></span>
            <span><span className={`badge badge-${statusCfg.tone}`} style={{ fontSize: 10 }}><span className="badge-dot" />{statusCfg.label}</span></span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              {r.assigned_to_name ? (
                <>
                  <Avatar name={r.assigned_to_name} id={r.assigned_to} size={20} />
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.assigned_to_name}</span>
                </>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>Unassigned</span>
              )}
            </div>
            <span style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{r.created_at ? formatRelativeTime(r.created_at) : '—'}</span>
          </div>
        )
      })}
    </div>
  )
}

// ── Timeline view ─────────────────────────────────────────────────────────────
function RequestTimeline({ requests, onOpen }) {
  if (requests.length === 0) return <div className="req-dropzone" style={{ marginTop: 4 }}>No requests</div>
  const sorted = [...requests].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
  return (
    <div className="card card-pad-sm" style={{ maxWidth: 760 }}>
      {sorted.map((r, i) => {
        const statusCfg = STATUS_CONFIG[r.status] || { label: r.status, tone: 'warning' }
        const clientName = r.company_name || r.client_name || '—'
        const dotColor = `var(${TONE_VAR[statusCfg.tone] || '--warning'})`
        return (
          <div key={r.id} className="req-tl-row" onClick={() => onOpen(r.id)} style={{ display: 'flex', gap: 12, cursor: 'pointer' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, boxShadow: `0 0 8px ${dotColor}`, marginTop: 7, flexShrink: 0 }} />
              {i < sorted.length - 1 && <span style={{ flex: 1, width: 1, background: 'var(--border)', marginTop: 4 }} />}
            </div>
            <div style={{ paddingBottom: 18, flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{r.title}</span>
                <span className={`badge badge-${statusCfg.tone}`} style={{ fontSize: 10 }}>{statusCfg.label}</span>
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 3 }}>
                {clientName} · {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────────
const RequestManager = () => {
  const [view, setView] = useState('board') // board | list | timeline
  const [filterPriority, setFilterPriority] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [filterStatus, setFilterStatus] = useState('') // '' = all active; 'archived' = archived (list only)
  const [showFilters, setShowFilters] = useState(false)
  const [modalStatus, setModalStatus] = useState(null) // non-null → NewRequestModal open
  const [modalClientId, setModalClientId] = useState(null)
  const [selectedRequestId, setSelectedRequestId] = useState(null)
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  // Auto-open a request (?open=id) or a new-request modal for a client (?newFor=id)
  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) setSelectedRequestId(openId)
    const newFor = searchParams.get('newFor')
    if (newFor) { setModalClientId(newFor); setModalStatus('pending') }
  }, [searchParams])

  // Active requests (the backend excludes archived unless ?status=archived is sent).
  // A high limit keeps client-side board/list/timeline filtering working off the
  // full set rather than the default page of 20.
  const { data: requests, isLoading } = useQuery(
    'requests',
    () => apiService.get('/requests?limit=500').then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  )

  // Archived requests are fetched separately, only when the Archived filter is
  // active in the list view (board never shows archived).
  const viewingArchived = filterStatus === 'archived'
  const { data: archivedRequests, isLoading: archivedLoading } = useQuery(
    ['requests', 'archived'],
    () => apiService.get('/requests?status=archived&limit=500').then(res => res.data),
    { staleTime: 60 * 1000, enabled: viewingArchived }
  )

  const displayRequests = Array.isArray(requests) ? requests : []

  // Shared priority/category predicate
  const matchesPriCat = (r) => {
    const matchesPriority = !filterPriority || r.priority === filterPriority
    const matchesCategory = !filterCategory || (r.category || '').toLowerCase() === filterCategory
    return matchesPriority && matchesCategory
  }

  // Active set filtered by priority/category (board always uses this)
  const filtered = displayRequests.filter(matchesPriCat)

  // The list/timeline honour the status filter too: 'archived' swaps in the
  // archived dataset; any other status narrows the active set to that status.
  const listSource = viewingArchived ? (Array.isArray(archivedRequests) ? archivedRequests : []) : displayRequests
  const listFiltered = listSource.filter(r => {
    if (!matchesPriCat(r)) return false
    if (!filterStatus || viewingArchived) return true
    return colOf(r.status) === colOf(filterStatus)
  })

  const kanbanCols = KANBAN_COLS.map(col => ({
    ...col,
    items: filtered.filter(r => col.statuses.includes(r.status)),
  }))

  const totalOpen = filtered.filter(r => ['open', 'pending'].includes(r.status)).length
  const totalActive = filtered.filter(r => ['in-progress', 'approved', 'in_progress', 'in-review'].includes(r.status)).length
  const activeFilterCount = (filterPriority ? 1 : 0) + (filterCategory ? 1 : 0) + (filterStatus ? 1 : 0)

  // Selecting Archived only makes sense in the list view — switch to it.
  const changeStatus = (s) => {
    setFilterStatus(s)
    if (s === 'archived' && view === 'board') setView('list')
  }

  const closeModal = () => { setModalStatus(null); setModalClientId(null) }

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Inbox</div>
          <h1>Requests</h1>
          <div className="subtitle">
            {totalOpen > 0 ? `${totalOpen} open` : 'No open requests'}
            {totalActive > 0 && ` · ${totalActive} in progress`}
          </div>
        </div>
        <div className="flex gap-2 items-center header-actions" style={{ position: 'relative' }}>
          <div className="tabs">
            <button className={view === 'board' ? 'active' : ''} onClick={() => setView('board')}>Board</button>
            <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>List</button>
            <button className={view === 'timeline' ? 'active' : ''} onClick={() => setView('timeline')}>Timeline</button>
          </div>
          <button className="btn" onClick={() => setShowFilters(s => !s)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconFilter /> Filters{activeFilterCount ? ` · ${activeFilterCount}` : ''}
          </button>
          <button className="btn btn-primary" onClick={() => { setModalClientId(null); setModalStatus('pending') }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconPlus /> New request
          </button>
          {showFilters && (
            <FiltersPopover
              priority={filterPriority} setPriority={setFilterPriority}
              category={filterCategory} setCategory={setFilterCategory}
              status={filterStatus} setStatus={changeStatus}
              onClose={() => setShowFilters(false)}
            />
          )}
        </div>
      </div>

      <div className="page-body">
        {isLoading ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
            Loading requests…
          </div>
        ) : view === 'board' ? (
          <div className="req-board">
            {kanbanCols.map(col => (
              <div key={col.id} className="req-col">
                <div className="req-col-head">
                  <span className={`badge badge-${col.tone}`}><span className="badge-dot" /> {col.label}</span>
                  <span className="req-col-count">{col.items.length}</span>
                  <button
                    className="site-action"
                    style={{ width: 22, height: 22 }}
                    title={`New request in ${col.label}`}
                    onClick={() => { setModalClientId(null); setModalStatus(STATUS_FOR_COL[col.id]) }}
                  >
                    <IconPlus size={12} />
                  </button>
                </div>
                {col.items.map(r => (
                  <KanbanCard key={r.id} request={r} onClick={() => setSelectedRequestId(r.id)} />
                ))}
                {col.items.length === 0 && (
                  <div className="req-dropzone">No requests</div>
                )}
              </div>
            ))}
          </div>
        ) : view === 'list' ? (
          viewingArchived && archivedLoading ? (
            <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>Loading archived…</div>
          ) : (
            <RequestList requests={listFiltered} onOpen={setSelectedRequestId} />
          )
        ) : (
          viewingArchived && archivedLoading ? (
            <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>Loading archived…</div>
          ) : (
            <RequestTimeline requests={listFiltered} onOpen={setSelectedRequestId} />
          )
        )}
      </div>

      {/* Request detail drawer */}
      {selectedRequestId && (
        <RequestDrawer
          requestId={selectedRequestId}
          onClose={() => setSelectedRequestId(null)}
          onUpdated={() => queryClient.invalidateQueries('requests')}
        />
      )}

      {/* New request modal */}
      {modalStatus && (
        <NewRequestModal
          initialStatus={modalStatus}
          initialClientId={modalClientId}
          onClose={closeModal}
          onCreated={() => queryClient.invalidateQueries('requests')}
        />
      )}
    </>
  )
}

export default RequestManager
